import { initializeApp } from "firebase/app";
import { getMessaging, isSupported } from "firebase/messaging";

const firebaseConfig = {
  apiKey: process.env.MIX_FIREBASE_CLOUD_MESSAGING_API_KEY,
  authDomain: process.env.MIX_FIREBASE_CLOUD_MESSAGING_AUTH_DOMAIN,
  projectId: process.env.MIX_FIREBASE_CLOUD_MESSAGING_PROJECT_ID,
  storageBucket: process.env.MIX_FIREBASE_CLOUD_MESSAGING_STORAGE_BUCKET,
  messagingSenderId: process.env.MIX_FIREBASE_CLOUD_MESSAGING_SENDER_ID,
  appId: process.env.MIX_FIREBASE_CLOUD_MESSAGING_APP_ID,
};

// グローバルなFirebase初期化情報を保存するオブジェクトを初期化
window.firebaseInitInfo = {
  appInitialized: false,
  messagingSupported: null,
  messagingInitialized: false,
  appName: null,
  hasMessagingApp: false
};

let app;

try {
  app = initializeApp(firebaseConfig);

  window.firebaseInitInfo.appInitialized = true;
  window.firebaseInitInfo.appName = app.name;
} catch (error) {
  console.error('[Firebase] initializeApp failed:', error);
  console.error('[Firebase] Error name:', error.name);
  console.error('[Firebase] Error message:', error.message);

  // エラー情報を保存
  window.firebaseInitError = {
    stage: 'initializeApp',
    name: error.name,
    message: error.message,
    code: error.code,
    stack: error.stack
  };
}

/**
 * Messagingインスタンスを生成する
 *
 * getMessaging() はブラウザ対応判定を内部で非同期に行い、非対応環境では
 * 呼び出し元の try/catch が届かない場所で messaging/unsupported-browser を throw する
 * （= 未処理のPromise rejectionとして残り、インスタンス自体は同期的に返ってしまう）。
 * そのため isSupported() で先に判定し、対応環境でのみ getMessaging() を呼ぶ。
 *
 * @returns {Promise<import("firebase/messaging").Messaging|null>} 非対応・初期化失敗時はnull
 */
const initMessaging = async () => {
  if (!app) {
    return null;
  }

  let supported = false;

  try {
    supported = await isSupported();
  } catch (error) {
    console.warn('[Firebase] isSupported() failed. Skip FCM setup:', error);

    window.firebaseInitError = {
      stage: 'isSupported',
      name: error.name,
      message: error.message,
      code: error.code,
      stack: error.stack
    };

    return null;
  }

  window.firebaseInitInfo.messagingSupported = supported;

  if (!supported) {
    console.warn('[Firebase] Messaging is not supported in this browser. Skip FCM setup.');

    window.firebaseInitError = {
      stage: 'isSupported',
      name: 'FirebaseError',
      message: "This browser doesn't support the API's required to use the Firebase SDK.",
      code: 'messaging/unsupported-browser',
      stack: null
    };

    return null;
  }

  try {
    const messaging = getMessaging(app);

    window.firebaseInitInfo.messagingInitialized = true;
    window.firebaseInitInfo.hasMessagingApp = !!messaging?.app;

    return messaging;
  } catch (error) {
    console.error('[Firebase] getMessaging failed:', error);
    console.error('[Firebase] Error name:', error.name);
    console.error('[Firebase] Error message:', error.message);

    // エラー情報を保存
    window.firebaseInitError = {
      stage: 'getMessaging',
      name: error.name,
      message: error.message,
      code: error.code,
      stack: error.stack
    };

    return null;
  }
};

let messagingPromise = null;

/**
 * Messagingインスタンスを取得する（初回のみ初期化し、以降は同じPromiseを返す）
 *
 * @returns {Promise<import("firebase/messaging").Messaging|null>} 非対応・初期化失敗時はnull
 */
export const getMessagingIfSupported = () => {
  if (!messagingPromise) {
    messagingPromise = initMessaging();
  }

  return messagingPromise;
};
